import * as THREE from "three";

/**
 * Replay your last run as a translucent "ghost ship" flying alongside.
 *
 * Persistence: positional samples are compacted to [x, y, z, t, …] and
 * stored under `aether.ghost`. Capped at ~600 samples (≈ 30s at 20 Hz).
 *
 * Playback: linear interpolation between bracketing samples by elapsed
 * run time. The ghost vanishes once the playback exceeds the saved
 * duration (i.e. you've already out-flown your previous self).
 */

const KEY = "aether.ghost";
const SAMPLE_HZ = 20;
const MAX_SAMPLES = 600;

interface Snapshot {
  /** Flat [x, y, z, t, x, y, z, t, …]. Length = sampleCount * 4. */
  data: Float32Array;
  count: number;
}

export class GhostShip {
  readonly mesh: THREE.Group;
  private mat: THREE.MeshStandardMaterial;
  private snapshot: Snapshot | null = null;
  // Per-run recording buffer
  private buf: number[] = [];
  private lastSampleAt = 0;
  private recording = false;

  constructor() {
    this.mesh = new THREE.Group();

    // Minimal ghost rep — small translucent cone + halo. Avoids cloning the
    // full ship mesh tree so material edits, palette changes etc. don't
    // cascade into the ghost.
    const hullGeo = new THREE.ConeGeometry(0.45, 1.6, 6);
    hullGeo.rotateX(Math.PI / 2);
    this.mat = new THREE.MeshStandardMaterial({
      color: 0x121826,
      emissive: 0x66ffff,
      emissiveIntensity: 0.9,
      transparent: true,
      opacity: 0.42,
    });
    const hull = new THREE.Mesh(hullGeo, this.mat);
    this.mesh.add(hull);

    const haloGeo = new THREE.RingGeometry(0.65, 0.85, 32);
    const haloMat = new THREE.MeshBasicMaterial({
      color: 0x88ffff,
      transparent: true,
      opacity: 0.25,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const halo = new THREE.Mesh(haloGeo, haloMat);
    halo.position.z = 0.65;
    this.mesh.add(halo);

    this.mesh.visible = false;
    this.loadSnapshot();
  }

  /** Begin a new recording. Call from game.start(). */
  startRecording() {
    this.buf = [];
    this.lastSampleAt = -Infinity;
    this.recording = true;
  }

  /**
   * Sample the current ship position if it's time. Called every frame
   * with engine-clock-relative run time.
   */
  sample(shipPos: THREE.Vector3, runT: number) {
    if (!this.recording) return;
    if (runT - this.lastSampleAt < 1 / SAMPLE_HZ) return;
    if (this.buf.length / 4 >= MAX_SAMPLES) return;
    this.buf.push(shipPos.x, shipPos.y, shipPos.z, runT);
    this.lastSampleAt = runT;
  }

  /** Finalize the recording. Call from game.end() / onRunEnd. */
  stopRecording() {
    if (!this.recording) return;
    this.recording = false;
    if (this.buf.length < 8) return; // less than 2 samples — not worth saving
    try {
      const arr = Array.from(this.buf);
      localStorage.setItem(KEY, JSON.stringify(arr));
      // Hot-load so the next run picks it up without a page reload.
      this.snapshot = {
        data: new Float32Array(arr),
        count: arr.length / 4,
      };
    } catch {
      /* quota or private mode — ghost simply won't persist */
    }
  }

  /**
   * Update ghost render position from the saved snapshot. Pass the
   * elapsed time since the current run started. Returns true if the
   * ghost is visible this frame.
   */
  update(runT: number, enabled: boolean): boolean {
    if (!enabled || !this.snapshot || this.snapshot.count < 2) {
      this.mesh.visible = false;
      return false;
    }
    const { data, count } = this.snapshot;
    const lastT = data[(count - 1) * 4 + 3];
    if (runT > lastT) {
      this.mesh.visible = false;
      return false;
    }
    // Linear search is fine — 600 samples max, runs once/frame.
    let i = 0;
    while (i < count - 1 && data[(i + 1) * 4 + 3] < runT) i++;
    const t0 = data[i * 4 + 3];
    const t1 = data[(i + 1) * 4 + 3];
    const k = t1 > t0 ? (runT - t0) / (t1 - t0) : 0;
    this.mesh.position.set(
      THREE.MathUtils.lerp(data[i * 4], data[(i + 1) * 4], k),
      THREE.MathUtils.lerp(data[i * 4 + 1], data[(i + 1) * 4 + 1], k),
      THREE.MathUtils.lerp(data[i * 4 + 2], data[(i + 1) * 4 + 2], k),
    );
    this.mesh.visible = true;
    return true;
  }

  hasSnapshot(): boolean {
    return this.snapshot !== null && this.snapshot.count >= 2;
  }

  private loadSnapshot() {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return;
      const arr = JSON.parse(raw) as number[];
      if (!Array.isArray(arr) || arr.length < 8 || arr.length % 4 !== 0) return;
      this.snapshot = { data: new Float32Array(arr), count: arr.length / 4 };
    } catch {
      /* corrupt entry — ignore */
    }
  }
}
